import { Box, Checkbox, IconButton, Tooltip } from "@mui/material"
import {
  AddOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpFilledOutlined,
  WarningOutlined,
} from "@mui-symbols-material/w300"
import { Link } from "@tanstack/react-router"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import type { ReactElement } from "react"

import { ProductActionMenu } from "./ProductActionsMenu"
import { useProductActionsStore } from "./stores/useProductsActions"
import { ProductActionsDialogs } from "./types/schema"

import { Button } from "@/components/common/Button"
import { LongWordWrapper } from "@/components/table/LongWordWrapper"
import { ProductStatusCell } from "@/components/table/ProductStatusCell"
import type { Product } from "@/graphql/codegen/graphql"
import { isProductsInfoValid } from "@/screens/Procurement/PurchaseOrder/utils/common"

const ActionsCell = ({ cell }: CellContext<Product, unknown>): string | ReactElement => {
  const { row } = cell.getContext()

  if (isProductsInfoValid(row.original)) {
    return ""
  }

  return (
    <Box
      className='flex w-full items-center justify-end space-x-4'
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <Link to='/app/product/$productId/edit' params={{ productId: row.original.productId }}>
        <Button appearance='outlined' variant='primary' startIcon={<AddOutlined />}>
          Product Details
        </Button>
      </Link>
      <ProductActionMenu {...row} />
      <Box className='w-8'>
        {row.getCanExpand() && (
          <IconButton aria-label='expand row' size='small' onClick={() => row.toggleExpanded()}>
            {row.getIsExpanded() ? <KeyboardArrowUpFilledOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        )}
      </Box>
    </Box>
  )
}

const SKUCell = ({ getValue, row }: CellContext<Product, unknown>): string | ReactElement => {
  const value = getValue() as string
  const setOpenedDialog = useProductActionsStore((state) => state.setOpenedDialog)
  const setProductAtAction = useProductActionsStore((state) => state.setProductAtAction)

  if (!value) {
    return (
      <Button
        appearance='text'
        variant='primary'
        startIcon={<WarningOutlined />}
        onClick={(e) => {
          e.stopPropagation()
          setProductAtAction(row.original)
          setOpenedDialog(ProductActionsDialogs.ADD_SKU)
        }}
      >
        Add SKU
      </Button>
    )
  }
  return <LongWordWrapper>{value}</LongWordWrapper>
}

const LocationCell = ({ row }: CellContext<Product, unknown>): string | ReactElement => {
  const value =
    row.original.locationsAssociations?.length > 0 ? row.original.locationsAssociations[0].shipping?.name : ""
  const setOpenedDialog = useProductActionsStore((state) => state.setOpenedDialog)
  const setProductAtAction = useProductActionsStore((state) => state.setProductAtAction)

  if (!value) {
    return (
      <Button
        appearance='text'
        variant='primary'
        startIcon={
          <Tooltip title='Choose Location in order to add Pricing to this product'>
            <WarningOutlined />
          </Tooltip>
        }
        onClick={(e) => {
          setProductAtAction(row.original)
          setOpenedDialog(ProductActionsDialogs.ADD_LOCATION)
          e.stopPropagation()
        }}
      >
        Add Location
      </Button>
    )
  }
  return value
}
export const companyProductListColumns: ColumnDef<Product>[] = [
  {
    accessorKey: "productId",
    id: "status",
    header: "",
    cell: ({ row }: CellContext<Product, unknown>): ReactElement => {
      return (
        <Box className='space-x-5'>
          <Checkbox
            id={row.id}
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            disabled={!row.getCanSelect()}
            onClick={(e) => {
              e.stopPropagation()
            }}
          />
        </Box>
      )
    },
    size: 50,
  },
  {
    header: "Product ID",
    accessorKey: "publicId",
    cell: ({ row }) => <ProductStatusCell product={row.original} />,
    enableSorting: true,
    sortingFn: "alphanumeric",
  },
  {
    accessorKey: "companyProductDescription",
    header: "Akrochem Description",
    enableSorting: true,
    sortingFn: "alphanumeric",
    cell: ({ row }: CellContext<Product, unknown>): ReactElement => (
      <LongWordWrapper>{row.original.companyProductDescription}</LongWordWrapper>
    ),
  },
  {
    accessorKey: "vendorSku",
    header: "Vendor SKU",
    cell: SKUCell,
    enableSorting: true,
    sortingFn: "alphanumeric",
  },
  {
    accessorKey: "vendorDescription",
    header: "Vendor Description",
    enableSorting: true,
    sortingFn: "alphanumeric",
    cell: ({ row }: CellContext<Product, unknown>): ReactElement => (
      <LongWordWrapper>{row.original.vendorDescription}</LongWordWrapper>
    ),
  },
  {
    id: "shipFromLocation",
    header: "Ship From",
    enableSorting: true,
    cell: LocationCell,
    sortingFn: "alphanumeric",
    accessorFn: (row) => row.locationsAssociations?.[0]?.shipping?.name || "",
  },
  {
    accessorKey: "actions",
    header: "",
    cell: ActionsCell,
    size: 325,
  },
]
